// Imported as components, not urls: the glyphs are drawn in `currentColor`
// so one asset covers idle, hover and active, and the button sets the colour.
import { ReactComponent as CursorIcon } from "./icons/cursor.svg";
import { ReactComponent as TextIcon } from "./icons/text.svg";
import { ReactComponent as AnnotateIcon } from "./icons/annotate.svg";
import { ReactComponent as CommentIcon } from "./icons/comment.svg";
import { ReactComponent as StickyIcon } from "./icons/sticky.svg";
import { ReactComponent as ShapesIcon } from "./icons/shapes.svg";
import { ReactComponent as LineIcon } from "./icons/shape-line.svg";
import { ReactComponent as SquareIcon } from "./icons/shape-square.svg";
import { ReactComponent as TriangleIcon } from "./icons/shape-triangle.svg";
import { ReactComponent as CircleIcon } from "./icons/shape-circle.svg";
import { STANDE } from "../standeTokens";

/**
 * What the board can do, in one place.
 *
 * The toolbar, the canvas and the keyboard shortcuts all read from here, so a
 * tool cannot exist in one and not the others. Adding a tool is adding a row.
 */
export const TOOLS = {
  SELECT: "select",
  TEXT: "text",
  DRAW: "draw",
  COMMENT: "comment",
  STICKY: "sticky",
  SHAPE: "shape",
};

/** `flyout` names the panel a tool opens instead of acting immediately. */
export const TOOL_LIST = [
  { id: TOOLS.SELECT, label: "Select", icon: CursorIcon, shortcut: "v" },
  { id: TOOLS.TEXT, label: "Text", icon: TextIcon, shortcut: "t" },
  { id: TOOLS.DRAW, label: "Draw", icon: AnnotateIcon, shortcut: "d" },
  { id: TOOLS.COMMENT, label: "Comment", icon: CommentIcon, shortcut: "c" },
  { id: TOOLS.STICKY, label: "Sticky note", icon: StickyIcon, shortcut: "n" },
  { id: TOOLS.SHAPE, label: "Shape", icon: ShapesIcon, shortcut: "s", flyout: "shapes" },
];

export const SHAPES = {
  LINE: "line",
  SQUARE: "square",
  TRIANGLE: "triangle",
  CIRCLE: "circle",
};

export const SHAPE_LIST = [
  { id: SHAPES.LINE, label: "Line", icon: LineIcon },
  { id: SHAPES.SQUARE, label: "Square", icon: SquareIcon },
  { id: SHAPES.TRIANGLE, label: "Triangle", icon: TriangleIcon },
  { id: SHAPES.CIRCLE, label: "Circle", icon: CircleIcon },
];

/**
 * The palette a teacher draws with, from the brand tokens. The pink is the
 * one value in the design that has no token of its own; it is the light-red
 * tint taken to a strength that works as a fill rather than a wash.
 */
export const COLOURS = [
  { id: "orange", value: STANDE.secondaryOrange, label: "Orange" },
  { id: "blue", value: STANDE.primaryBlue, label: "Blue" },
  { id: "cream", value: STANDE.lightYellow, label: "Cream" },
  { id: "pink", value: "#ff726c", label: "Pink" },
];

export const DEFAULT_COLOUR = COLOURS[0].value;

/** Sticky notes read better on the soft tints than on the solid brand hues. */
export const STICKY_TINTS = {
  [STANDE.secondaryOrange]: STANDE.lightRed,
  [STANDE.primaryBlue]: "#e4f9ff",
  [STANDE.lightYellow]: STANDE.lightYellow,
  "#ff726c": STANDE.lightRed,
};

export const stickyTint = (colour) => STICKY_TINTS[colour] || STANDE.lightYellow;

/** Which tools place something by clicking the canvas. */
export const PLACES_ON_CLICK = new Set([TOOLS.TEXT, TOOLS.STICKY, TOOLS.COMMENT]);

/** Which tools draw by dragging. */
export const DRAGS_TO_DRAW = new Set([TOOLS.DRAW, TOOLS.SHAPE]);

/**
 * Tools that go back to the cursor once they have placed one thing.
 *
 * Placing is a one-shot action: after dropping a note you almost always want
 * to move or type in it, and staying armed means the next click drops a
 * second note by accident. Drawing is the exception — a pen stays a pen,
 * because strokes come in runs.
 */
export const REVERTS_AFTER_USE = new Set([
  TOOLS.TEXT,
  TOOLS.STICKY,
  TOOLS.COMMENT,
  TOOLS.SHAPE,
]);
